/**
 * Best-effort extraction of a numeric value from CC's pricingLabel strings
 * like "$250", "£100", "$150 AUD", "Starting at $450". Falls back to 0 if
 * no number is found. Used for the GA4 `value` field on conversion events
 * and for the cart subtotal on promo validation.
 */
export function parsePrice(label: string): number {
  if (!label) return 0;
  const m = label.replace(/,/g, '').match(/[£$]\s*(\d+(?:\.\d+)?)/);
  return m && m[1] ? Number(m[1]) : 0;
}

/**
 * Extract the first currency amount from a pricingLabel — as a formatted
 * string that preserves the currency symbol + suffix. E.g.:
 *   "$175 flat fitting fee"                        → "$175"
 *   "£100 flat fitting fee"                        → "£100"
 *   "FREE w/ ... or $150 AUD flat fitting fee"     → "$150 AUD"
 */
function extractPriceToken(label: string): string {
  const m = label.match(/([£$])\s*(\d+(?:\.\d+)?)(\s+[A-Z]{2,3})?/);
  if (!m) return '';
  return `${m[1]}${m[2]}${m[3] ?? ''}`;
}

/**
 * Two-line display split for a pricingLabel. Two supported patterns:
 *
 *   US/AU "FREE w/ custom club purchase or $X flat fitting fee"
 *     → line 1: **FREE** - Fitting fee w/ custom club purchase
 *       line 2: **$X** - Fitting fee w/out custom club purchase
 *
 *   UK "£X flat fitting fee"  (no FREE-with-purchase concept in the UK menu)
 *     → line 1: **£X** flat fitting fee
 *       line 2: (empty)
 *
 *   Callaway QL "FREE" (no numeric price)
 *     → line 1: **FREE**
 *       line 2: (empty)
 *
 * The bold portion is returned separately so callers can wrap it in <strong>.
 */
export function formatPricingLines(
  label: string,
  showFlatFee = false,
): {
  line1: { bold: string; rest: string };
  line2: { bold: string; rest: string };
} {
  // Two-price form: "<with purchase> w/ custom club purchase or <flat> flat
  // fitting fee". The with-purchase side is FREE outside a promotion and a
  // figure during one ("$100 w/ custom club purchase or $400 flat fitting
  // fee"), so both are matched here.
  //
  // Handling only the FREE spelling is what put the wrong price on the live
  // site: a promo label carries no "FREE", fell through to the single-price
  // branch below, and that branch takes the FIRST amount in the string — so
  // "$100 w/ custom club purchase or $400 flat fitting fee" rendered as
  // "$100 flat fitting fee". The promo price was shown as the flat fee, the
  // real flat fee disappeared, and the purchase condition went with it.
  const twoPrice = label.match(
    /(FREE|[£$]\s*\d+(?:\.\d+)?(?:\s+[A-Z]{2,3})?)\s+w\/\s*custom club purchase\s+or\s+([£$]\s*\d+(?:\.\d+)?(?:\s+[A-Z]{2,3})?)\s+flat fitting fee/i,
  );
  if (twoPrice) {
    const isFree = twoPrice[1].toUpperCase().startsWith('FREE');
    const withPurchase = isFree ? 'FREE' : twoPrice[1].replace(/\s+/g, ' ').trim();

    // A promotional price is shown on its own. Naming the price without a
    // purchase beside it undercuts the promotion at the moment the customer is
    // choosing, and it was never asked for — the ask was to state the purchase
    // condition on the promotional price, which line 1 does.
    //
    // The FREE form keeps both lines. That is long-standing behaviour outside a
    // promotion window, where the second line is the only price on the card and
    // omitting it would leave the fitting looking unconditionally free.
    // …unless the market shows both numbers. The UK and Australian sites
    // present the promotional price against the flat fee, so a card showing
    // one disagrees with the site around it. See promoPriceShowsFlatFee() in
    // config/countries.ts — the caller passes the market's answer.
    if (!isFree && !showFlatFee) {
      return {
        line1: { bold: withPurchase, rest: ' - Fitting fee w/ custom club purchase' },
        line2: { bold: '', rest: '' },
      };
    }

    return {
      line1: { bold: withPurchase, rest: ' - Fitting fee w/ custom club purchase' },
      line2: {
        bold: twoPrice[2].replace(/\s+/g, ' ').trim(),
        rest: ' - Fitting fee w/out custom club purchase',
      },
    };
  }

  const price = extractPriceToken(label);

  if (price) {
    // UK-style flat-fee-only pricing
    return {
      line1: { bold: price, rest: ' flat fitting fee' },
      line2: { bold: '', rest: '' },
    };
  }

  // No price token — Callaway QL Challenge "FREE" case
  return {
    line1: { bold: 'FREE', rest: '' },
    line2: { bold: '', rest: '' },
  };
}
